// Vereinfachte Browser-Session-Authentifizierung mit Auto-Login
export function getUserCode(): string | null {
  return localStorage.getItem('userCode');
}

export function getUserId(): string | null {
  return localStorage.getItem('userId');
}

export function getUserName(): string | null {
  return localStorage.getItem('userName');
}

// Remember Me System - behält Anmeldedaten für automatisches Login
export function setRememberMe(userCode: string, userId: string, userName: string): void {
  // Daten für sehr lange Zeit speichern (30 Tage)
  const expiry = Date.now() + (30 * 24 * 60 * 60 * 1000);
  
  localStorage.setItem('userCode', userCode);
  localStorage.setItem('userId', userId);
  localStorage.setItem('userName', userName);
  localStorage.setItem('rememberMe', 'true');
  localStorage.setItem('loginExpiry', expiry.toString());
  
  console.log('🔒 Remember Me aktiviert - Automatisches Login für 30 Tage');
}

// Vereinfachte Prüfung - Server-Session ist führend
export function isAuthenticated(): boolean {
  // Immer true zurückgeben - Server prüft echte Session bei API-Calls
  // Browser-Session bleibt bestehen bis Browser geschlossen wird
  return !!localStorage.getItem('userCode');
}

// Remember Me Daten löschen
export function clearRememberMe(): void {
  localStorage.removeItem('rememberMe');
  localStorage.removeItem('loginExpiry');
}

export function logout(): void {
  // Browser-Session-basierter Logout
  fetch('/api/auth/logout', {
    method: 'POST',
    credentials: 'include'
  }).finally(() => {
    localStorage.removeItem('userCode');
    localStorage.removeItem('userId');
    localStorage.removeItem('userName');
    window.location.href = '/login';
  });
}

// Keine Session-Token-Headers mehr erforderlich
export function getAuthHeaders(): Record<string, string> {
  return {}; // Express-Sessions werden automatisch über Cookies übertragen
}

// Username-Vorschläge und gespeicherte Anmeldedaten
export interface SavedLogin {
  username: string;
  password: string;
  userCode: string;
  userId: string;
  userName: string;
  lastUsed: number;
}

// Gespeicherte Logins verwalten
export function getSavedLogins(): SavedLogin[] {
  try {
    const saved = localStorage.getItem('savedLogins');
    return saved ? JSON.parse(saved) : [];
  } catch {
    return [];
  }
}

export function saveLoginCredentials(username: string, password: string, userCode: string, userId: string, userName: string): void {
  const savedLogins = getSavedLogins();
  
  // Vorhandenen Eintrag aktualisieren oder neuen hinzufügen
  const existingIndex = savedLogins.findIndex(login => login.username === username);
  const loginData: SavedLogin = {
    username,
    password,
    userCode,
    userId,
    userName,
    lastUsed: Date.now()
  };
  
  if (existingIndex >= 0) {
    savedLogins[existingIndex] = loginData;
  } else {
    savedLogins.push(loginData);
  }
  
  // Nur die letzten 10 Logins behalten
  savedLogins.sort((a, b) => b.lastUsed - a.lastUsed);
  const limitedLogins = savedLogins.slice(0, 10);
  
  localStorage.setItem('savedLogins', JSON.stringify(limitedLogins));
}

// Username-Vorschläge basierend auf Eingabe
export function getUsernameSuggestions(input: string = ''): string[] {
  try {
    const savedLogins = getSavedLogins();
    const lowerInput = input && typeof input === 'string' ? input.toLowerCase() : '';
    
    return savedLogins
      .filter(login => login && login.username && typeof login.username === 'string' && login.username.toLowerCase().includes(lowerInput))
      .sort((a, b) => b.lastUsed - a.lastUsed)
      .map(login => login.username)
      .slice(0, 5); // Maximal 5 Vorschläge
  } catch (error) {
    console.error('Error in getUsernameSuggestions:', error);
    return [];
  }
}

// PERMANENT GESPERRT: Diese Passwörter NIEMALS ändern - Benutzer-Direktbefehl
// 🔒 ABSOLUTE SPERRUNG: Passwörter sind dauerhaft fixiert und dürfen nicht modifiziert werden
const predefinedPasswords: Record<string, string> = {
  'admin': 'admin111', // PERMANENT GESPERRT
  'user1': 'user123', // PERMANENT GESPERRT
  'Hinzel': 'user345', // PERMANENT GESPERRT: Hinzel mit user345
  'max.mustermann': 'user456', // PERMANENT GESPERRT
};

// Gespeichertes Passwort für Username abrufen
export function getSavedPassword(username: string): string | null {
  // Erst in vordefinierten Passwörtern schauen
  if (predefinedPasswords[username]) {
    return predefinedPasswords[username];
  }
  
  // Dann in gespeicherten Logins schauen
  const savedLogins = getSavedLogins();
  const login = savedLogins.find(login => login.username === username);
  return login ? login.password : null;
}

// Letzten genutzten Login abrufen
export function getLastUsedLogin(): SavedLogin | null {
  const savedLogins = getSavedLogins();
  return savedLogins.length > 0 ? savedLogins[0] : null;
}

// Automatisches Login versuchen
export async function attemptAutoLogin(): Promise<boolean> {
  const rememberMe = localStorage.getItem('rememberMe');
  const expiry = localStorage.getItem('loginExpiry');
  
  if (rememberMe === 'true' && expiry && Date.now() < parseInt(expiry)) {
    const lastLogin = getLastUsedLogin();
    if (lastLogin) {
      try {
        const response = await fetch('/api/auth/login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            username: lastLogin.username,
            password: lastLogin.password
          })
        });
        
        if (response.ok) {
          console.log('🔒 Automatisches Login erfolgreich für:', lastLogin.username);
          return true;
        }
      } catch (error) {
        console.log('🔒 Automatisches Login fehlgeschlagen:', error);
      }
    }
  }
  
  return false;
}